import type { RouteLocationNormalizedLoaded } from 'vue-router'
import type { TourStep } from '@/components/common/TourOverlay.vue'

/**
 * Per-page guided tours — "Vodič kroz ovu stranicu", the ? button in the header.
 * Each tour explains ONE page in detail and never leaves it: steps carry no `route`,
 * only a `selector` (a `data-tour` anchor on that page) and, where the part of the
 * page is behind a tab, an `activate` selector that opens it first.
 *
 * Strings live under `pageTour.<id>.<step>.{tag,title,body}` (i18n/locales/<lang>/pageTour.ts).
 * TourOverlay skips any step whose anchor is missing or hidden, which is what makes one
 * tour per page work for every permission level — a read-only operator simply sees
 * fewer steps (no "new contract", no status buttons, no save).
 */
export type PageTourId = 'dashboard' | 'contractEditor' | 'contractEditHeader' | 'position'

/**
 * Build a step from its anchor and i18n key — keeps the per-page lists short.
 * `activate` is a selector clicked first (a tab) so the anchor becomes visible.
 */
export function step(id: PageTourId, key: string, selector: string, activate?: string): TourStep {
    return {
        selector,
        ...(activate ? { activate } : {}),
        tagKey: `pageTour.${id}.${key}.tag`,
        titleKey: `pageTour.${id}.${key}.title`,
        bodyKey: `pageTour.${id}.${key}.body`,
    }
}

const TAB_BINS = '[data-tour="tab-bins"]'
const TAB_AUDIT = '[data-tour="tab-audit"]'

export const pageTours: Record<PageTourId, TourStep[]> = {
    // "/" — aggregate exposure card on top, contract list left, form/summary right.
    // `form` and `summary` are mutually exclusive (one of them is skipped).
    dashboard: [
        step('dashboard', 'aggregate', '[data-tour="aggregate-card"]'),
        step('dashboard', 'toolbar', '[data-tour="aggregate-toolbar"]'),
        step('dashboard', 'refresh', '[data-tour="aggregate-refresh"]'),
        step('dashboard', 'list', '[data-tour="contract-list"]'),
        step('dashboard', 'filters', '[data-tour="contract-filters"]'),
        step('dashboard', 'newContract', '[data-tour="new-contract"]'),
        step('dashboard', 'viewPosition', '[data-tour="view-position"]'),
        step('dashboard', 'form', '[data-tour="contract-form"]'),
        step('dashboard', 'summary', '[data-tour="contract-summary"]'),
    ],

    // "/contracts/:id" — header, status workflow, the bin grid (Bins tab), audit (Audit tab).
    // Every Bins-tab step re-activates that tab so stepping back from Audit works.
    contractEditor: [
        step('contractEditor', 'header', '[data-tour="editor-header"]'),
        step('contractEditor', 'status', '[data-tour="status-actions"]'),
        step('contractEditor', 'dayNav', '[data-tour="day-nav"]', TAB_BINS),
        step('contractEditor', 'grid', '[data-tour="bin-grid"]', TAB_BINS),
        step('contractEditor', 'toolbar', '[data-tour="bin-toolbar"]', TAB_BINS),
        step('contractEditor', 'liveChart', '[data-tour="live-chart"]', TAB_BINS),
        step('contractEditor', 'footer', '[data-tour="editor-footer"]', TAB_BINS),
        step('contractEditor', 'audit', '[data-tour="audit-tab"]', TAB_AUDIT),
    ],

    // "/contracts/:id/edit" — the header form in amend mode (ContractForm anchors).
    contractEditHeader: [
        step('contractEditHeader', 'form', '[data-tour="contract-form"]'),
        step('contractEditHeader', 'counterparty', '[data-tour="form-counterparty"]'),
        step('contractEditHeader', 'country', '[data-tour="form-country"]'),
        step('contractEditHeader', 'sideProduct', '[data-tour="form-side-product"]'),
        step('contractEditHeader', 'delivery', '[data-tour="form-delivery"]'),
        step('contractEditHeader', 'profileSource', '[data-tour="form-profile-source"]'),
        step('contractEditHeader', 'volume', '[data-tour="form-volume"]'),
        step('contractEditHeader', 'submit', '[data-tour="form-submit"]'),
    ],

    // "/positions/:contract_id" — read-only per-contract position. `noSnapshot` and the
    // chart steps are mutually exclusive (the empty state replaces the chart).
    position: [
        step('position', 'header', '[data-tour="position-header"]'),
        step('position', 'noSnapshot', '[data-tour="position-no-snapshot"]'),
        step('position', 'dayNav', '[data-tour="position-day-nav"]'),
        step('position', 'toggles', '[data-tour="position-toggles"]'),
        step('position', 'chart', '[data-tour="position-chart"]'),
        step('position', 'summary', '[data-tour="position-summary"]'),
        step('position', 'reference', '[data-tour="profile-reference"]'),
    ],
}

// Route path → tour. Order matters where prefixes overlap (/edit before :id).
const routeTours: Array<[RegExp, PageTourId]> = [
    [/^\/$/, 'dashboard'],
    [/^\/contracts\/[^/]+\/edit$/, 'contractEditHeader'],
    [/^\/contracts\/[^/]+$/, 'contractEditor'],
    [/^\/positions\/[^/]+$/, 'position'],
]

/** The tour for the current route, or null on a page that has none (unauthorized, …). */
export function pageTourIdFor(route: RouteLocationNormalizedLoaded): PageTourId | null {
    const hit = routeTours.find(([re]) => re.test(route.path))
    return hit ? hit[1] : null
}
